import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { apiClient } from "@/lib/apiClient";

/**
 * Waiting-list matches for the journal (waitlist README §4.6): 60s poll
 * (useNotifications pattern; the server response is cached by data
 * versions, so polls are free between changes) + a sonner toast when a
 * NEW match appears (dedupe by entry+slot).
 */

export type WaitlistMatch = {
  entry: {
    id: number; client_name: string; client_phone: string;
    client_email?: string | null; service_id: number;
    service_title?: string | null; staff_id: number | null;
    staff_name?: string | null;
    /** admin-chosen master subset (README §2 staff_ids) — null = any / pinned */
    staff_ids?: number[] | null; staff_names?: string[] | null;
    date_from: string; date_to: string;
    time_pref: string; duration_min?: number | null;
    status: string; note?: string | null;
    notify_count: number; source: string;
    created_by_id?: number | null; created_by_name?: string | null;
  };
  slot: { staff_id: number; staff_name?: string | null; date: string; start: string };
};

export type WaitlistMatchesResponse = {
  matches: WaitlistMatch[];
  waiting: WaitlistMatch["entry"][];
  active_count: number;
};

export const WAITLIST_MATCHES_KEY = ["waitlist", "matches"];

export function useWaitlistMatches(
  enabled = true,
  opts: { withToasts?: boolean } = {},
) {
  const { withToasts = true } = opts;
  const { t } = useTranslation();
  const seen = React.useRef<Set<string>>(new Set());
  const primed = React.useRef(false);

  const query = useQuery<WaitlistMatchesResponse>({
    queryKey: WAITLIST_MATCHES_KEY,
    queryFn: async () =>
      (await apiClient.get("/api/v1/company/waitlist/matches")).data,
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: 1,
    enabled,
  });

  React.useEffect(() => {
    // Toasts fire from exactly ONE always-mounted instance (the sidebar);
    // other readers share the react-query cache without re-toasting.
    if (!withToasts) return;
    const matches = query.data?.matches ?? [];
    const ids = matches.map(
      (m) => `${m.entry.id}::${m.slot.date}::${m.slot.start}`);
    if (!primed.current) {
      // first load: prime the dedupe set silently (no toast storm on mount)
      ids.forEach((id) => seen.current.add(id));
      primed.current = matches.length >= 0 && query.isSuccess;
      return;
    }
    for (let i = 0; i < ids.length; i++) {
      if (seen.current.has(ids[i])) continue;
      seen.current.add(ids[i]);
      const m = matches[i];
      toast.info(
        t("waitlist.toast_match",
          "{{client}} is waiting for {{service}} — a matching slot is free on {{date}} at {{start}}",
          { client: m.entry.client_name, service: m.entry.service_title ?? "",
            date: m.slot.date, start: m.slot.start }),
        // a match is an actionable event, not a notification — stays until
        // the admin dismisses it
        { id: ids[i], duration: Infinity, closeButton: true },
      );
    }
  }, [query.data, query.isSuccess, t, withToasts]);

  return query;
}

/** Call after a cancel/delete mutation succeeds — the cancel-moment prompt
 * (README §4.6) should not wait for the next poll. */
export function useWaitlistRefetch() {
  const qc = useQueryClient();
  return React.useCallback(
    () => qc.invalidateQueries({ queryKey: WAITLIST_MATCHES_KEY }),
    [qc]);
}
